import { StateCreator, create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { User } from '../models/user.model';

export type ImpersonationStoreSlice = {
  isImpersonating: boolean;
  originalToken: string;
  originalRefreshToken: string;
  originalUser: User;
  impersonatedBusinessName: string;
  startImpersonation: (
    originalToken: string,
    originalRefreshToken: string,
    originalUser: User,
    businessName: string
  ) => void;
  endImpersonation: () => void;
};

export const DefaultImpersonationStore = {
  isImpersonating: false,
  originalToken: '',
  originalRefreshToken: '',
  originalUser: {} as User,
  impersonatedBusinessName: '',
};

// Guard: the impersonation store is persisted, so a session that
// stored a non-sadmin user as `originalUser` (either from an old
// bug or from manually tampered localStorage) survives across
// reloads and every `Retornar a Sadmin` restore keeps 403'ing.
// Only accept a snapshot whose user carries the `super_admin`
// role — otherwise startImpersonation is a no-op and the return
// button will short-circuit via the same predicate.
export const isSuperAdminUser = (user: User | undefined | null): boolean => {
  if (!user) return false;
  const role = (user as unknown as { role?: unknown }).role;
  if (typeof role === 'string') return role === 'super_admin';
  if (role && typeof role === 'object') {
    const name = (role as { name?: unknown }).name;
    if (name === 'super_admin') return true;
  }
  // Fallback: the backend login response includes `roleId` but not
  // always an expanded `role.name`. Resolve via the rol store when
  // available so a fresh sadmin session (before the FE enriches the
  // user object) still passes the check. Guarded by import-cycle
  // avoidance — read the store synchronously with getState().
  const roleId = (user as unknown as { roleId?: unknown }).roleId;
  if (typeof roleId === 'string' && roleId) {
    try {
      // Lazy require to avoid circular import between the auth
      // hydration chain and this pure predicate module.
       
      const rolStore = require('./rol.store') as {
        useRolStore: {
          getState: () => { roles: { id: string; name: string }[] };
        };
      };
      const roles = rolStore.useRolStore.getState().roles;
      const match = Array.isArray(roles)
        ? roles.find((r) => r.id === roleId)
        : undefined;
      if (match?.name === 'super_admin') return true;
    } catch {
      // Store not initialized — treat as unknown role.
    }
  }
  return false;
};

const impersonationStateSlice: StateCreator<
  ImpersonationStoreSlice,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  ImpersonationStoreSlice
> = (set) => ({
  ...DefaultImpersonationStore,
  startImpersonation: (
    originalToken: string,
    originalRefreshToken: string,
    originalUser: User,
    businessName: string
  ) => {
    // Refuse to persist a non-sadmin identity as the "original" —
    // guarantees the return path can only restore a real sadmin
    // session. Log the drop so we notice if a caller is passing
    // corrupted state instead of silently accepting it.
    if (
      !originalToken ||
      !originalRefreshToken ||
      !isSuperAdminUser(originalUser)
    ) {
      console.warn(
        '[impersonation] startImpersonation refused: originalUser is not super_admin or tokens missing.',
        {
          hasToken: !!originalToken,
          role: (originalUser as unknown as { role?: unknown })?.role,
        }
      );
      return;
    }
    set(
      () => ({
        isImpersonating: true,
        originalToken,
        originalRefreshToken,
        originalUser,
        impersonatedBusinessName: businessName,
      }),
      undefined,
      'impersonation/start'
    );
  },
  endImpersonation: () =>
    set(() => DefaultImpersonationStore, undefined, 'impersonation/end'),
});

export const useImpersonationStore = create<ImpersonationStoreSlice>()(
  devtools(
    persist(impersonationStateSlice, {
      name: 'impersonation',
      // Migration-on-hydrate: purge any persisted snapshot whose
      // originalUser is not super_admin. Prevents a corrupted prior
      // session from surviving a reload.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (state.isImpersonating && !isSuperAdminUser(state.originalUser)) {
          console.warn(
            '[impersonation] Purging corrupted persisted state: originalUser is not super_admin.'
          );
          Object.assign(state, DefaultImpersonationStore);
        }
      },
    }),
    { name: 'impersonation-store' }
  )
);
